import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { IDS } from "@/lib/testIds";
import { BUSINESS, IMAGES } from "@/lib/data";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/bridal", label: "Bridal" },
  { to: "/gallery", label: "Gallery" },
  { to: "/reviews", label: "Reviews" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const onHome = pathname === "/";
  const isDark = onHome && !scrolled;

  return (
    <>
      <motion.header
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "bg-[#F3EAD9]/90 backdrop-blur-md border-b border-black/10"
            : onHome
              ? "bg-transparent"
              : "bg-[#F3EAD9]/90 backdrop-blur-md border-b border-black/10"
        }`}
      >
        <div className="max-w-[1600px] mx-auto flex items-center justify-between px-6 md:px-10 h-[72px] md:h-[84px]">
          <Link
            to="/"
            data-testid={IDS.navBrand}
            className="flex items-center gap-3 group"
          >
            <span
              className={`w-10 h-10 md:w-11 md:h-11 rounded-full overflow-hidden flex items-center justify-center transition-colors ${
                isDark ? "bg-[#F3EAD9]" : "bg-[#1B1B1B]"
              }`}
            >
              <img
                src={IMAGES.logo}
                alt="Good Looks logo"
                className="w-full h-full object-cover"
              />
            </span>
            <span
              className={`font-display text-xl md:text-2xl tracking-tight transition-colors ${
                isDark ? "text-[#F3EAD9]" : "text-[#1B1B1B]"
              }`}
            >
              Good Looks
            </span>
            <span
              className={`hidden md:inline-block h-3 w-px ${
                isDark ? "bg-white/30" : "bg-black/30"
              }`}
            />
            <span
              className={`hidden md:inline-block eyebrow ${
                isDark ? "text-white/70" : "text-black/60"
              }`}
            >
              Mansa · Punjab
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-[13px] tracking-wide font-medium transition-colors gl-link ${
                    isDark ? "text-white/85 hover:text-white" : "text-black/75 hover:text-black"
                  } ${isActive ? (isDark ? "!text-[#C89B3C]" : "!text-[#C89B3C]") : ""}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={BUSINESS.phoneHref}
              data-testid="nav-call-btn"
              className={`hidden md:inline-flex items-center gap-2 text-[12px] tracking-widest uppercase font-medium ${
                isDark ? "text-white/85 hover:text-[#C89B3C]" : "text-black/75 hover:text-[#C89B3C]"
              } transition-colors`}
            >
              <Phone size={14} strokeWidth={1.5} />
              Call
            </a>
            <Link
              to="/book"
              data-testid={IDS.navBookBtn}
              className="hidden md:inline-flex btn-gold !py-[10px] !px-5"
            >
              <span>Book Now</span>
            </Link>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid={IDS.navMobileToggle}
              aria-label="Toggle menu"
              className={`lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border transition-colors ${
                isDark
                  ? "border-white/25 text-white hover:bg-white/10"
                  : "border-black/15 text-black hover:bg-black/5"
              }`}
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            data-testid={IDS.navMobileMenu}
            className="fixed inset-0 z-40 bg-[#1B1B1B] text-[#F3EAD9] lg:hidden"
          >
            <div className="h-full flex flex-col justify-between pt-28 pb-14 px-8">
              <nav className="flex flex-col gap-4">
                {LINKS.map((l, i) => (
                  <motion.div
                    key={l.to}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.15 + i * 0.05 }}
                  >
                    <NavLink
                      to={l.to}
                      data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                      className="font-display text-4xl leading-tight text-white/90 hover:text-[#C89B3C] transition-colors"
                    >
                      <span className="text-[#C89B3C] text-sm align-super mr-3">0{i + 1}</span>
                      {l.label}
                    </NavLink>
                  </motion.div>
                ))}
              </nav>
              <div className="space-y-4">
                <Link to="/book" className="btn-gold w-full justify-center">
                  <span>Book Appointment</span>
                </Link>
                <a href={BUSINESS.phoneHref} className="btn-ghost w-full justify-center">
                  <Phone size={14} /> <span>{BUSINESS.phone}</span>
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
